import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (v) => {
  if (!v) return '';
  try {
    return new Date(v).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
  } catch {
    return String(v);
  }
};

/**
 * Avisa cuando aparece (o desaparece) un servicio web en un puerto que antes no estaba.
 * Solo se renderiza si hay cambios sin revisar.
 */
const SurfaceChangesPanel = () => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/surface-changes?only_pending=true`, { headers: auth() });
        setItems(res.data.items || []);
      } catch (e) {
        setItems([]);
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, []);

  const ack = async (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    try {
      await axios.put(`${API}/surface-changes/${id}/ack`, {}, { headers: auth() });
    } catch (e) {
      console.error('ack surface change', e);
    }
  };

  if (!loaded || items.length === 0) return null;

  const nuevos = items.filter((i) => i.change_type === 'new_service');

  return (
    <div
      className="mt-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5 shadow-sm"
      data-testid="surface-changes-panel"
    >
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base md:text-lg font-bold text-amber-900 flex items-center">
            <span className="mr-2 h-6 w-6 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center">
              !
            </span>
            Cambió tu superficie expuesta a internet
          </h3>
          <p className="text-sm text-amber-800 mt-1">
            {nuevos.length > 0
              ? `Detectamos ${nuevos.length} servicio${nuevos.length === 1 ? '' : 's'} web que antes no estaba${nuevos.length === 1 ? '' : 'n'} publicado${nuevos.length === 1 ? '' : 's'}.`
              : 'Detectamos cambios en los servicios publicados.'}
          </p>
        </div>
        <span className="text-xs font-semibold text-amber-900 bg-amber-200 px-3 py-1 rounded-full">
          {items.length} sin revisar
        </span>
      </div>

      <div className="mt-4 space-y-2" data-testid="surface-changes-list">
        {items.map((c) => (
          <div
            key={c.id}
            className="flex items-center justify-between flex-wrap gap-3 bg-white border border-amber-200 rounded-lg px-4 py-3"
            data-testid={`surface-change-${c.id}`}
          >
            <div className="min-w-0">
              <p className="font-mono text-sm font-semibold text-gray-900">
                {c.scheme}://{c.host}:{c.port}
              </p>
              <p className="text-xs text-gray-600 truncate">
                {c.change_type === 'new_service' ? (
                  <span className="font-semibold text-amber-700">Servicio nuevo</span>
                ) : (
                  <span className="font-semibold text-gray-500">Dejó de responder</span>
                )}
                {c.title ? ` · ${c.title}` : ''}
                {c.server ? ` · ${c.server}` : ''}
                {c.status_code ? ` · HTTP ${c.status_code}` : ''}
                {` · ${fmt(c.detected_at)}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {c.change_type === 'new_service' && (
                <a
                  href={`${c.scheme}://${c.host}:${c.port}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-3 py-1 rounded-md border border-amber-400 text-amber-800 hover:bg-amber-50"
                  data-testid={`surface-change-open-${c.id}`}
                >
                  Ver qué es
                </a>
              )}
              <button
                onClick={() => ack(c.id)}
                className="text-xs px-3 py-1 rounded-md bg-amber-600 text-white font-medium hover:bg-amber-700"
                data-testid={`surface-change-ack-${c.id}`}
              >
                Ya lo revisé
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-amber-800 mt-3">
        Un servicio que aparece sin aviso suele ser un panel de administración, una aplicación de
        prueba o un equipo publicado por error. Si no debería estar expuesto, cerralo.
      </p>
    </div>
  );
};

export default SurfaceChangesPanel;
